
import React, { useState, useEffect } from 'react';
import { ApiKey, Project, apiService } from '../../services/apiService';

const KeyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
  </svg>
);

const ProjectIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const SetupInProgressIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

interface SettingsTabProps {
  apiKey: ApiKey | null;
  onNavigateToTab?: (tabName: string) => void;
}
  
export default function SettingsTab({ apiKey, onNavigateToTab }: SettingsTabProps) {
  const [keyCopied, setKeyCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchProjects = async () => {
      try {

        if (!apiKey) {
          setLoading(false);
          return;
        }
        
        const userProjects = await apiService.getAllProjects(apiKey.user_id);
        setProjects(userProjects);
        

        const currentProject = userProjects.find(p => p.id === apiKey.project_id);
        if (currentProject) {
          setSelectedProject(currentProject);
        } else if (userProjects.length > 0) {
          setSelectedProject(userProjects[0]);
        }
      } catch (error) {
        
        setErrorMessage("Failed to fetch projects. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [apiKey]);

  const copyApiKey = () => {
    if (!apiKey) return;
    
    navigator.clipboard.writeText(apiKey.key);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleRegenerateClick = () => {
    setShowConfirmation(true);
  };

  const confirmRegeneration = async () => {
    setShowConfirmation(false);
    setRegenerating(true);
    try {
      if (selectedProject && apiKey) {
        await apiService.regenerateApiKey(apiKey.user_id, selectedProject.id);
        window.location.reload();
      } else {
        throw new Error("No project selected or API key not available");
      }
    } catch (error) {
      
      setErrorMessage('Failed to regenerate API key');
      setRegenerating(false);
    }
  };

  const cancelRegeneration = () => {
    setShowConfirmation(false);
  };

  const handleProjectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = event.target.value;
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setSelectedProject(project);
    }
  };


  const handleGoToOverview = () => {
    if (onNavigateToTab) {
      onNavigateToTab('overview');
    }
  };


  if (!apiKey) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '70vh',
        textAlign: 'center',
        padding: '0 2rem'
      }}>
        <SetupInProgressIcon />
        <h2 style={{
          marginTop: '1.5rem',
          marginBottom: '1rem',
          fontSize: '1.5rem',
          fontWeight: '500'
        }}>
          Complete your setup first
        </h2>
        <p style={{
          color: 'var(--text-muted)',
          maxWidth: '500px',
          marginBottom: '1.5rem'
        }}>
          Settings will be available after you generate your API key in the Overview tab.
        </p>
        <button 
          className="button-primary"
          onClick={handleGoToOverview}
        >
          Go to Overview
        </button>
      </div>
    );
  }

  return (
    <div>
      {errorMessage && (
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.1)', 
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px', 
          padding: '0.75rem 1rem', 
          marginBottom: '1.5rem', 
          color: 'var(--error)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <AlertIcon />
          <span>{errorMessage}</span>
        </div>
      )}
      
      {}
      {projects.length > 1 && (
        <div className="dashboard-card">
          <h2 className="card-title">
            <ProjectIcon />
            Project Selection
          </h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Select Project</label>
            <select 
              className="form-input"
              value={selectedProject?.id || ''}
              onChange={handleProjectChange}
              disabled={loading}
            >
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <p style={{ 
              marginTop: '0.75rem', 
              fontSize: '0.85rem', 
              color: 'var(--text-muted)'
            }}>
              Select a project to view or regenerate its API key.
            </p>
          </div>
        </div>
      )}
      
      <div className="dashboard-card">
        <h2 className="card-title">
          <KeyIcon />
          API Key Management
          {selectedProject && <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            - {selectedProject.name}
          </span>}
        </h2>
      
        <div style={{ marginBottom: '1.75rem' }}>
          <label className="form-label">Your API Key</label>
          <div className="key-row">
            <input
              type="text"
              readOnly
              value={apiKey.key}
              className="key-input"
            />
            <button onClick={copyApiKey} className="copy-button">
              {keyCopied ? <CheckIcon /> : <CopyIcon />}
              <span>{keyCopied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
          <p style={{ 
            marginTop: '0.75rem', 
            fontSize: '0.85rem', 
            color: 'var(--text-muted)'
          }}>
            Your API key grants full access to the selected project. Keep it secure and never share it publicly.
          </p>
        </div>
        
        <div>
          <h3 style={{ 
            fontSize: '1rem', 
            marginBottom: '0.75rem', 
            fontWeight: '500'
          }}>
            Regenerate API Key
          </h3>
          <p style={{ 
            fontSize: '0.9rem', 
            color: 'var(--text-muted)', 
            marginBottom: '1.25rem' 
          }}>
            If your API key has been compromised, you should regenerate it immediately. 
            Note that your current key will stop working once a new one is generated.
          </p>
          
          {showConfirmation ? (
            <div className="confirmation-dialog">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                <AlertIcon />
                <div>
                  <h4 style={{ marginBottom: '0.5rem' }}>Are you sure?</h4>
                  <p>This will invalidate your current API key for {selectedProject?.name}. All applications using the old key will stop working until you update them with the new key.</p>
                </div>
              </div>
              <div className="dialog-actions">
                <button onClick={cancelRegeneration} className="button-secondary">
                  Cancel
                </button>
                <button onClick={confirmRegeneration} className="button-primary danger-button">
                  <RefreshIcon />
                  <span>Regenerate</span>
                </button>
              </div>
            </div>
          ) : (
            <button 
              className="button-primary"
              onClick={handleRegenerateClick}
              disabled={regenerating || !selectedProject}
              style={{ maxWidth: '300px' }}
            >
              {regenerating ? (
                <div className="button-loading">
                  <span className="spinner"></span>
                  <span>Regenerating...</span>
                </div>
              ) : (
                <>
                  <RefreshIcon />
                  <span>Regenerate API Key</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      {}
      <div className="dashboard-card">
        <h2 className="card-title">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Security Settings
        </h2>
        <div style={{ opacity: '0.5' }}>
          <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
            Advanced security settings will be available in a future update.
          </p>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0.75rem' 
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '0.75rem 1rem',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              border: '1px solid var(--border-light)'
            }}>
              <div>
                <h4 style={{ fontSize: '0.9rem', fontWeight: '500' }}>IP Restrictions</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Limit API access to specific IP addresses</p>
              </div>
              <div>
                <div style={{ 
                  width: '40px', 
                  height: '20px', 
                  background: 'rgba(255,255,255,0.1)', 
                  borderRadius: '10px',
                  position: 'relative',
                  cursor: 'not-allowed'
                }}>
                  <div style={{ 
                    position: 'absolute', 
                    left: '3px', 
                    top: '3px', 
                    width: '14px', 
                    height: '14px', 
                    background: 'var(--text-muted)', 
                    borderRadius: '50%' 
                  }}></div>
                </div>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '0.75rem 1rem',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              border: '1px solid var(--border-light)'
            }}>
              <div>
                <h4 style={{ fontSize: '0.9rem', fontWeight: '500' }}>Usage Limits</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Set maximum daily token usage</p>
              </div>
              <div>
                <div style={{ 
                  width: '40px', 
                  height: '20px', 
                  background: 'rgba(255,255,255,0.1)', 
                  borderRadius: '10px',
                  position: 'relative',
                  cursor: 'not-allowed'
                }}>
                  <div style={{ 
                    position: 'absolute', 
                    left: '3px', 
                    top: '3px', 
                    width: '14px', 
                    height: '14px', 
                    background: 'var(--text-muted)', 
                    borderRadius: '50%' 
                  }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}